import React from "react";
import { Waves, TriangleAlert, MapPin, Sunrise } from "lucide-react";

// Highlighted "defining event" timeline card for the 1992 Pacific tsunami.
// Rendered in place of the normal <Article> for id="tsunami-1992" so the town's
// pivotal moment visually stands apart from the other history chapters.
const CONTENT = {
  en: {
    eyebrow: "The event that defined Popoyo",
    date: "September 1, 1992",
    title: "The 1992 Tsunami",
    lead:
      "Just before midnight the Pacific coast of Nicaragua was struck by one of the most destructive events in the region's memory. For Popoyo it is the single dividing line between the old fishing village and the town that stands today.",
    steps: [
      { icon: TriangleAlert, tag: "~23:16 · Sept 1", title: "A magnitude-7.7 quake offshore", text: "A powerful \u201Ctsunami earthquake\u201D ruptured beneath the ocean floor roughly 100 km off the Nicaraguan coast — slow and quiet on land, but violent under the sea." },
      { icon: Waves, tag: "Minutes later", title: "The wave comes ashore", text: "A tsunami reaching several metres surged across the Rivas shoreline, sweeping away nearly the entire original village of Popoyo in the dark." },
      { icon: MapPin, tag: "The aftermath", title: "Survivors move to higher ground", text: "Those who remained rebuilt inland and uphill, relocating the community to safer ground to the south of Playa Sardinas." },
      { icon: Sunrise, tag: "Today", title: "The coast we know now", text: "That relocation drew the map of modern Popoyo and neighbouring Guasacate — a community shaped, in every sense, by the sea." },
    ],
  },
  es: {
    eyebrow: "El evento que definió Popoyo",
    date: "1 de septiembre de 1992",
    title: "El tsunami de 1992",
    lead:
      "Poco antes de la medianoche, la costa del Pacífico de Nicaragua sufrió uno de los eventos más destructivos de la memoria de la región. Para Popoyo es la línea que separa el viejo pueblo pesquero del pueblo que existe hoy.",
    steps: [
      { icon: TriangleAlert, tag: "~23:16 · 1 sep", title: "Un sismo de magnitud 7.7 mar adentro", text: "Un poderoso \u201Cterremoto tsunami\u201D se rompió bajo el fondo marino a unos 100 km de la costa nicaragüense — lento y silencioso en tierra, pero violento bajo el mar." },
      { icon: Waves, tag: "Minutos después", title: "La ola llega a la orilla", text: "Un tsunami de varios metros arrasó la costa de Rivas y se llevó, en la oscuridad, casi todo el pueblo original de Popoyo." },
      { icon: MapPin, tag: "Las secuelas", title: "Los sobrevivientes suben a terreno más alto", text: "Quienes quedaron reconstruyeron tierra adentro y en alto, reubicando la comunidad en terreno más seguro al sur de Playa Sardinas." },
      { icon: Sunrise, tag: "Hoy", title: "La costa que hoy conocemos", text: "Esa reubicación dibujó el mapa del Popoyo moderno y de la vecina Guasacate — una comunidad moldeada, en todo sentido, por el mar." },
    ],
  },
  fr: {
    eyebrow: "L'événement qui a défini Popoyo",
    date: "1er septembre 1992",
    title: "Le tsunami de 1992",
    lead:
      "Peu avant minuit, la côte pacifique du Nicaragua a subi l'un des événements les plus destructeurs de la mémoire régionale. Pour Popoyo, c'est la ligne qui sépare l'ancien village de pêcheurs du village d'aujourd'hui.",
    steps: [
      { icon: TriangleAlert, tag: "~23h16 · 1er sept", title: "Un séisme de magnitude 7,7 au large", text: "Un puissant \u00AB séisme tsunami \u00BB s'est rompu sous le plancher océanique à environ 100 km de la côte nicaraguayenne — lent et discret à terre, mais violent sous la mer." },
      { icon: Waves, tag: "Quelques minutes plus tard", title: "La vague atteint le rivage", text: "Un tsunami de plusieurs mètres a déferlé sur le littoral de Rivas, emportant dans l'obscurité la quasi-totalité du village d'origine de Popoyo." },
      { icon: MapPin, tag: "Les conséquences", title: "Les survivants gagnent les hauteurs", text: "Ceux qui restaient ont reconstruit plus haut et à l'intérieur des terres, déplaçant la communauté vers un terrain plus sûr, au sud de Playa Sardinas." },
      { icon: Sunrise, tag: "Aujourd'hui", title: "La côte que nous connaissons", text: "Ce déplacement a dessiné la carte du Popoyo moderne et de la voisine Guasacate — une communauté façonnée, à tous égards, par la mer." },
    ],
  },
  de: {
    eyebrow: "Das Ereignis, das Popoyo prägte",
    date: "1. September 1992",
    title: "Der Tsunami von 1992",
    lead:
      "Kurz vor Mitternacht traf eines der zerstörerischsten Ereignisse der Region die Pazifikküste Nicaraguas. Für Popoyo ist es die Trennlinie zwischen dem alten Fischerdorf und dem heutigen Ort.",
    steps: [
      { icon: TriangleAlert, tag: "~23:16 · 1. Sept", title: "Ein Beben der Stärke 7,7 vor der Küste", text: "Ein starkes \u201ETsunami-Beben\u201C brach rund 100 km vor der nicaraguanischen Küste unter dem Meeresboden auf — an Land langsam und leise, unter Wasser jedoch heftig." },
      { icon: Waves, tag: "Minuten später", title: "Die Welle erreicht die Küste", text: "Ein mehrere Meter hoher Tsunami überrollte die Küste von Rivas und riss im Dunkeln fast das gesamte ursprüngliche Dorf Popoyo mit sich." },
      { icon: MapPin, tag: "Die Folgen", title: "Überlebende ziehen ins Hochland", text: "Die Verbliebenen bauten landeinwärts und höher gelegen neu auf und verlegten die Gemeinde auf sichereren Boden südlich von Playa Sardinas." },
      { icon: Sunrise, tag: "Heute", title: "Die Küste, die wir heute kennen", text: "Diese Umsiedlung zeichnete die Karte des modernen Popoyo und des benachbarten Guasacate — eine Gemeinde, die in jeder Hinsicht vom Meer geformt wurde." },
    ],
  },
  zh: {
    eyebrow: "定义了波波约的事件",
    date: "1992 年 9 月 1 日",
    title: "1992 年海啸",
    lead:
      "临近午夜，尼加拉瓜太平洋沿岸遭遇了该地区记忆中最具破坏性的事件之一。对波波约而言，这是划分旧渔村与今日小镇的分界线。",
    steps: [
      { icon: TriangleAlert, tag: "约 23:16 · 9 月 1 日", title: "近海 7.7 级地震", text: "一场强烈的“海啸地震”在距尼加拉瓜海岸约 100 公里的海床下破裂——在陆地上缓慢而无声，在海面之下却极为猛烈。" },
      { icon: Waves, tag: "数分钟后", title: "海浪涌上岸", text: "高达数米的海啸席卷里瓦斯海岸，在黑暗中几乎卷走了波波约的整个原始村庄。" },
      { icon: MapPin, tag: "灾后", title: "幸存者迁往高地", text: "留下来的人在内陆和高处重建，将社区迁到萨尔迪纳斯海滩以南更安全的地带。" },
      { icon: Sunrise, tag: "今天", title: "我们如今熟知的海岸", text: "那次迁移勾勒出今日波波约与邻近瓜萨卡特的格局——一个在各种意义上都被大海塑造的社区。" },
    ],
  },
  it: {
    eyebrow: "L'evento che ha definito Popoyo",
    date: "1º settembre 1992",
    title: "Lo tsunami del 1992",
    lead:
      "Poco prima di mezzanotte la costa pacifica del Nicaragua fu colpita da uno degli eventi più distruttivi nella memoria della regione. Per Popoyo è la linea che separa il vecchio villaggio di pescatori dal paese di oggi.",
    steps: [
      { icon: TriangleAlert, tag: "~23:16 · 1 set", title: "Un sisma di magnitudo 7,7 al largo", text: "Un potente \u201Cterremoto tsunami\u201D si ruppe sotto il fondale a circa 100 km dalla costa nicaraguense — lento e silenzioso sulla terraferma, ma violento sotto il mare." },
      { icon: Waves, tag: "Pochi minuti dopo", title: "L'onda raggiunge la riva", text: "Uno tsunami di diversi metri travolse la costa di Rivas, spazzando via nel buio quasi l'intero villaggio originario di Popoyo." },
      { icon: MapPin, tag: "Le conseguenze", title: "I sopravvissuti salgono in alto", text: "Chi rimase ricostruì più all'interno e in alto, spostando la comunità su terreno più sicuro a sud di Playa Sardinas." },
      { icon: Sunrise, tag: "Oggi", title: "La costa che conosciamo oggi", text: "Quel trasferimento ha disegnato la mappa della moderna Popoyo e della vicina Guasacate — una comunità plasmata, in ogni senso, dal mare." },
    ],
  },
  ru: {
    eyebrow: "Событие, определившее Попойо",
    date: "1 сентября 1992 года",
    title: "Цунами 1992 года",
    lead:
      "Незадолго до полуночи тихоокеанское побережье Никарагуа пережило одно из самых разрушительных событий в памяти региона. Для Попойо это граница между старой рыбацкой деревней и посёлком, каким он существует сегодня.",
    steps: [
      { icon: TriangleAlert, tag: "~23:16 · 1 сент.", title: "Землетрясение магнитудой 7,7 в море", text: "Мощное \u00ABцунами-землетрясение\u00BB произошло под морским дном примерно в 100 км от побережья Никарагуа — медленное и тихое на суше, но яростное под водой." },
      { icon: Waves, tag: "Через несколько минут", title: "Волна выходит на берег", text: "Цунами высотой в несколько метров обрушилось на побережье Риваса и в темноте смыло почти всю первоначальную деревню Попойо." },
      { icon: MapPin, tag: "Последствия", title: "Выжившие уходят на возвышенность", text: "Оставшиеся отстроились вглубь материка и выше, переселив общину на более безопасную землю к югу от Плайя-Сардинас." },
      { icon: Sunrise, tag: "Сегодня", title: "Побережье, знакомое нам сейчас", text: "Это переселение задало карту современного Попойо и соседней Гуасакате — сообщества, во всех смыслах сформированного морем." },
    ],
  },
};

export default function TsunamiSpotlight({ lang = "en", image }) {
  const c = CONTENT[lang] || CONTENT.en;
  return (
    <article
      id="tsunami-1992"
      data-testid="article-tsunami-1992"
      className="relative overflow-hidden rounded-3xl scroll-mt-24 shadow-lg"
      style={{ background: "linear-gradient(135deg, #10233f 0%, #1B3B6F 55%, #264653 100%)", color: "#fff" }}
    >
      {/* Emergency accent rail */}
      <span aria-hidden className="absolute left-0 top-0 bottom-0 w-1.5" style={{ background: "var(--emergency, #E4572E)" }} />

      {/* Banner image with date overlay */}
      {image && (
        <div className="relative h-40 sm:h-52 w-full overflow-hidden">
          <img src={image} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover opacity-70" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(16,35,63,0.35) 0%, rgba(16,35,63,0.85) 100%)" }} />
          <div className="absolute left-6 sm:left-8 bottom-4">
            <p className="text-[11px] uppercase tracking-[0.2em] text-white/80 flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full animate-pulse" style={{ background: "var(--emergency, #E4572E)" }} />
              {c.eyebrow}
            </p>
            <p className="text-2xl sm:text-3xl font-bold leading-tight" style={{ fontFamily: "Outfit", textShadow: "0 2px 14px rgba(0,0,0,0.5)" }} data-testid="tsunami-date">
              {c.date}
            </p>
          </div>
        </div>
      )}

      <div className="px-6 sm:px-8 py-6 sm:py-8">
        <h2 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: "Outfit" }}>{c.title}</h2>
        <p className="mt-2 text-sm sm:text-base text-white/85 leading-relaxed max-w-3xl">{c.lead}</p>

        {/* Vertical timeline */}
        <ol className="mt-6 relative" data-testid="tsunami-timeline">
          <span aria-hidden className="absolute left-[19px] top-2 bottom-2 w-px bg-white/20" />
          {c.steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <li key={i} className="relative flex gap-4 pb-6 last:pb-0" data-testid={`tsunami-step-${i}`}>
                <span className="relative z-10 w-10 h-10 rounded-full flex items-center justify-center shrink-0 shadow-md" style={{ background: "rgba(255,255,255,0.10)", border: "1px solid rgba(255,255,255,0.22)" }}>
                  <Icon className="w-5 h-5 text-white" />
                </span>
                <div className="pt-0.5">
                  <p className="text-[11px] uppercase tracking-widest text-white/60">{s.tag}</p>
                  <p className="font-semibold leading-snug" style={{ fontFamily: "Outfit" }}>{s.title}</p>
                  <p className="text-sm text-white/80 mt-0.5 leading-relaxed max-w-2xl">{s.text}</p>
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </article>
  );
}
